// =====================================================================
// crmCustomer — Pilots: shared constants, helpers, and the operating
// checklist phase/task data. Sibling pilots-*.jsx files reference these
// by bareword; each is also attached to window at the bottom so the
// reference resolves through the global object (the standard cross-file
// pattern in this codebase, e.g. opportunities-stakeholders.jsx).
// =====================================================================

// ── Status enum (mirror of server-side ALLOWED_STATUSES) ─────────────
const PILOT_STATUSES = [
  { key: "Qualifying", tone: "slate", label: "Qualifying" },
  { key: "Active",     tone: "green", label: "Active" },
  { key: "AtDecision", tone: "amber", label: "At decision" },
  { key: "Converted",  tone: "blue",  label: "Converted" },
  { key: "Extension",  tone: "amber", label: "Extension" },
  { key: "Stopped",    tone: "red",   label: "Stopped" },
];

function pilotStatusTone(key) {
  const m = PILOT_STATUSES.find(s => s.key === key);
  return m ? m.tone : "slate";
}
function pilotStatusLabel(key) {
  const m = PILOT_STATUSES.find(s => s.key === key);
  return m ? m.label : key || "—";
}

// ── Pence helpers ────────────────────────────────────────────────────
// Currency lives in integer pence on the wire; the formatter prints £ at
// the boundary only (same approach as the churn dashboard).
function poundsFromPence(pence) {
  if (pence == null) return null;
  return Math.round(pence) / 100;
}
function fmtPounds(pence) {
  if (pence == null || isNaN(pence)) return "—";
  return "£" + (Math.round(pence) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

// ── Date arithmetic ──────────────────────────────────────────────────
function addDaysIso(iso, days) {
  const d = new Date(iso); d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// ── Operating checklist ──────────────────────────────────────────────
// Each phase mirrors a section of docs/pilot-checklist.html. Tasks carry
// an optional `c` (critical) flag and an optional `n` (note). Keys are
// `p<phaseIdx>-<taskIdx>` so the JSON state map stays human-debuggable.
const PILOT_CHECKLIST = [
  { tag: "Phase 00 · Before pilot", title: "Qualify and paper the deal",
    sub: "Done before kickoff. Get the right client, the right buyer, and the commercials and security in place first.",
    tasks: [
      { t: "Confirm ICP fit: regulated sector, 50–1,000 FTE, the buyer profile you sell to", n: "A pilot outside ICP teaches you nothing transferable.", c: true },
      { t: "Engage the economic buyer (CTO / Head of Eng / VP Eng), not only an enthusiastic engineer", n: "The user is not the signer. No budget owner engaged means no conversion.", c: true },
      { t: "Identify the internal champion who will defend the budget", n: "Note name and role on the pilot." },
      { t: "Agree the success criteria in writing with the client", n: "Undefined success means an ambiguous ending and a soft no.", c: true },
      { t: "Agree the three projects to be assessed in the pilot" },
      { t: "Confirm pricing and paper it: £3,000 pilot fee, full £12,000 annual starter stated, pilot fee credited to year one on conversion", n: "Stating the full price protects your ACV anchor.", c: true },
      { t: "Sign the pilot agreement and collect the £3,000 before kickoff", n: "Paid before start. Non-negotiable.", c: true },
      { t: "Fix the 90-day decision date, the 180-day continuation, and the weekly-extension terms in the agreement" },
      { t: "Complete security and privacy steps: NDA / DPA, security review, and consent to capture usage and outcome data", n: "Your own telemetry must not become a reason a security buyer hesitates.", c: true },
      { t: "Schedule kickoff and all monthly gate dates up front" },
      { t: "Agree a mutual action plan with the client through to the day-90 decision", c: true },
      { t: "Identify a second stakeholder beyond the champion, ideally one who holds or influences budget", c: true },
      { t: "Design the path to first value before kickoff: define the first useful output and the shortest route to it", c: true },
      { t: "Sequence the three projects: start with the one most likely to show a fast, visible win" },
      { t: "Confirm this pilot uses the same standard structure as the rest of the cohort" },
    ]},
  { tag: "Phase 01 · Setup", title: "Kickoff (Day 0)",
    sub: "The first 24–48 hours. The baseline captured here is the foundation of your entire value story.",
    tasks: [
      { t: "Score the baseline maturity (1–5) for each of the three projects against the TRUST framework, before the product touches them", n: "Non-negotiable. The delta vs this score is your core proof metric.", c: true },
      { t: "Record the baseline scores and the day-zero date on the pilot", c: true },
      { t: "Provision access and onboard the right users across the three projects" },
      { t: "Define what counts as 'first value' and instrument it" },
      { t: "Confirm usage telemetry is running: activation, active users, feature depth" },
      { t: "Log the economic buyer, champion, source and ICP fit on the pilot" },
      { t: "Run the kickoff session: restate the agreed success criteria and the day-90 decision" },
      { t: "Onboard concierge-style: sit with the team and walk them to first value" },
      { t: "Open an always-on feedback channel with the pilot team (shared Slack/Teams space)" },
    ]},
  { tag: "Phase 02 · During", title: "Monthly gates (Months 1 and 2)",
    sub: "Each gate is a go / no-go, not a passive check-in. The job is to catch a stall early and steer toward the decision.",
    tasks: [
      { t: "Month 1 gate: capture time-to-first-value, findings surfaced and actioned, engagement across the three projects" },
      { t: "Month 2 gate: capture the first maturity movement vs baseline, depth of use, and champion strength" },
      { t: "At each gate, record risks and blockers, and the single action owed before the next gate" },
      { t: "Keep the economic buyer engaged through the pilot, not only the users" },
      { t: "Open the conversion conversation before day 90 so the decision is never a surprise" },
      { t: "Capture verbatim customer quotes as they arise" },
      { t: "Run each gate as user research: watch what the team actually does, then ask why" },
      { t: "Log every product request and classify it: configuration for this client, or roadmap for everyone", c: true },
      { t: "Confirm more than one stakeholder stays engaged, not only the original champion" },
    ]},
  { tag: "Phase 03 · Day 90 / End", title: "The decision",
    sub: "The fundable event. Pilot value becomes revenue, a reference, and a metric for the data room.",
    tasks: [
      { t: "Re-score maturity for each project and record the delta vs baseline", c: true },
      { t: "Capture quantified, customer-attested time or cost saved, in writing", c: true },
      { t: "Run the decision: convert to the £12,000 annual starter (pilot fee credited) or stop", c: true },
      { t: "Confirm the economic buyer signs, not only the champion" },
      { t: "Secure reference consent and a verbatim testimonial while goodwill is highest", c: true },
      { t: "Capture the expansion signal: intent to grow 3 → 5 projects, more teams or pillars" },
      { t: "Build the case study now, while the detail is fresh" },
      { t: "Ask the client for two or three introductions to peers who fit the profile", c: true },
      { t: "If stopping, record why, honestly" },
      { t: "Update the dashboard for your investor snapshot" },
    ]},
  { tag: "Phase 04 · Extension", title: "The 30-day weekly bridge (if used)",
    sub: "A bridge to signing, never a rolling free continuation.",
    tasks: [
      { t: "Use the extension only while the annual contract is being finalised, with a defined end", c: true },
      { t: "Grant week by week, with each week tied to movement toward signing" },
      { t: "Record each extension week on the pilot log" },
      { t: "Keep capturing maturity and expansion data through the window" },
      { t: "Set the hard stop date; if unsigned by then, treat it as a no and capture the reason", c: true },
    ]},
];

// How the pilot was sourced. Mirrors ALLOWED_SOURCES in pilotsController.js.
const PILOT_SOURCES = ["Cold", "Warm", "Referral", "Inbound"];

// Gate types for the Gatelog tab. `dayOffset` is days from the pilot's
// start date — used to auto-populate the date when adding a new gate
// entry (the user can override). Keys match ALLOWED_GATES in
// tpCustBe/controllers/pilotGatesController.js.
const PILOT_GATES = [
  { key: "month1",          label: "Month 1 review",                  dayOffset: 30  },
  { key: "month2",          label: "Month 2 review",                  dayOffset: 60  },
  { key: "month3-decision", label: "Month 3 — PILOT END / decision",  dayOffset: 90  },
  { key: "ext1",            label: "Extension wk 1 (bridge to sign)", dayOffset: 97  },
  { key: "ext2",            label: "Extension wk 2 (bridge to sign)", dayOffset: 104 },
  { key: "ext3",            label: "Extension wk 3 (bridge to sign)", dayOffset: 111 },
  { key: "ext4",            label: "Extension wk 4 (bridge to sign)", dayOffset: 118 },
];

function pilotGateLabel(key) {
  const g = PILOT_GATES.find(x => x.key === key);
  return g ? g.label : key || "—";
}

// The gate immediately after `currentKey` in PILOT_GATES order — drives
// the "Mark Done" auto-create-next behaviour. Returns null past the last
// gate (ext4), and null for unknown keys.
function nextPilotGate(currentKey) {
  const idx = PILOT_GATES.findIndex(g => g.key === currentKey);
  if (idx < 0 || idx >= PILOT_GATES.length - 1) return null;
  return PILOT_GATES[idx + 1];
}

// Shared wrapper for the three auto-create-next-gate call sites
// (NewPilotDialog first-gate seed, EditGateDialog save, row Mark Done).
// Tolerates the BE's 409 "Unique constraint violation" — that means
// another path (concurrent tab, prior save) already inserted the row, so
// the user's intent is satisfied. Forces a gates reload either way so
// the cached D.PILOT_GATES picks up the winning row.
async function createNextGateSafely(payload) {
  try {
    await tpAPI.pilotGates.create(payload);
  } catch (err) {
    const msg = (err && err.message) || "";
    // The BE conflict message is stable (helpers/response.js); detect
    // either it or any 4xx that mentions "constraint" so a future BE
    // wording tweak doesn't silently re-fire the warning.
    if (/Unique constraint/i.test(msg) || /constraint/i.test(msg)) {
      if (typeof window.tpReload === "function") {
        try { await window.tpReload("pilotGates"); } catch (e) { /* non-fatal */ }
      }
      return;
    }
    console.warn("next-gate auto-create failed:", err);
  }
}

const PILOT_GATE_DECISIONS = [
  { key: "go",      label: "Go",      tone: "green" },
  { key: "no-go",   label: "No-go",   tone: "red"   },
  { key: "pending", label: "Pending", tone: "amber" },
];

function pilotGateDecisionTone(key) {
  const d = PILOT_GATE_DECISIONS.find(x => x.key === key);
  return d ? d.tone : "slate";
}
function pilotGateDecisionLabel(key) {
  const d = PILOT_GATE_DECISIONS.find(x => x.key === key);
  return d ? d.label : key || "—";
}

// ── Case study capture template ──────────────────────────────────────
// Mirrors docs/case-study-template.md. The per-pilot CaseStudyDialog
// (pilots-list.jsx) renders this declaratively and stores the filled
// values as a JSON blob on pilots.caseStudy. Filled incrementally over
// the pilot's life: baseline at kickoff, results at day 90, quote at
// close. Each field: { key, label, type, placeholder?, help?, rows?,
// options? }. type ∈ {text, textarea, date, select}.
const PILOT_CASE_STUDY_SECTIONS = [
  { key: "glance", title: "At a glance",
    note: "The headline facts. Check the consent line before using the client name publicly.",
    fields: [
      { key: "client",           label: "Client",            type: "text", placeholder: "Company name", help: "see consent before public use" },
      { key: "sector",           label: "Sector",            type: "text", placeholder: "Financial services / healthcare / critical infrastructure" },
      { key: "size",             label: "Size",              type: "text", placeholder: "Employee count or band" },
      { key: "projectsAssessed", label: "Projects assessed", type: "text", placeholder: "Project 1, Project 2, Project 3" },
      { key: "windowStart",      label: "Pilot window — kickoff", type: "date" },
      { key: "windowEnd",        label: "Pilot window — day 90",  type: "date" },
    ] },
  { key: "challenge", title: "The challenge",
    note: "Capture at kickoff. Two or three sentences on where the client was and why it mattered — their words, not yours.",
    fields: [
      { key: "challenge", label: "The situation before Trusted Path", type: "textarea", rows: 4,
        placeholder: "Where did security sit in how they shipped? What was the cost of leaving it there?" },
    ] },
  { key: "baseline", title: "The baseline",
    note: "Capture at kickoff, before the product touches the work.",
    fields: [
      { key: "baselineMaturity",  label: "Starting maturity (per project)", type: "text", placeholder: "P1, P2, P3 scores" },
      { key: "problemQuantified", label: "The problem, quantified", type: "textarea", rows: 3,
        placeholder: "e.g. threat modelling took X days per release; security review was a bottleneck of Y" },
    ] },
  { key: "whatWeDid", title: "What we did",
    note: "The pilot itself, in plain terms.",
    fields: [
      { key: "whatWeDid", label: "How the team used Trusted Path", type: "textarea", rows: 4,
        placeholder: "Across the three projects over ninety days — and what changed in how they worked." },
    ] },
  { key: "results", title: "The results",
    note: "Capture at day 90. These must match the numbers in the pilot log.",
    fields: [
      { key: "maturityUplift", label: "Maturity uplift",  type: "text", placeholder: "P1 delta, P2 delta, P3 delta" },
      { key: "timeCostSaved",  label: "Time or cost saved", type: "text", placeholder: "The customer-attested number" },
      { key: "findings",       label: "Findings surfaced and actioned", type: "text", placeholder: "Count or summary" },
      { key: "outcome",        label: "Outcome", type: "textarea", rows: 2,
        placeholder: "Converted to the £12,000 annual starter / expanded from 3 to X projects / other" },
    ] },
  { key: "quote", title: "In their words",
    note: "Capture at close, while goodwill is highest. One strong, specific verbatim line beats a paragraph of praise.",
    fields: [
      { key: "quote",            label: "Direct quote", type: "textarea", rows: 3, placeholder: "From the champion or economic buyer" },
      { key: "quoteAttribution", label: "Attributed to", type: "text", placeholder: "Name, role, Company (subject to consent)" },
    ] },
  { key: "consent", title: "Consent",
    note: "Naming a client publicly needs explicit permission — your buyers are security leaders who care how their information is handled.",
    fields: [
      { key: "consentApproved",   label: "Approved for use", type: "select", options: ["", "Yes", "No"] },
      { key: "consentApprovedBy", label: "Approved by",      type: "text", placeholder: "Name, role" },
      { key: "consentDate",       label: "Date",             type: "date" },
      { key: "consentLimits",     label: "Limits on use",    type: "textarea", rows: 2,
        placeholder: "e.g. logo only, no named individual / full attribution / anonymised only" },
    ] },
];

// Flat list of every case-study field key, in template order.
const CASE_STUDY_FIELD_KEYS = PILOT_CASE_STUDY_SECTIONS
  .reduce((acc, s) => acc.concat(s.fields.map(f => f.key)), []);

// How many fields the user has filled — drives the progress chip + bar.
function caseStudyProgress(cs) {
  const data = cs || {};
  let filled = 0;
  CASE_STUDY_FIELD_KEYS.forEach(k => {
    if (data[k] != null && String(data[k]).trim() !== "") filled++;
  });
  return { filled, total: CASE_STUDY_FIELD_KEYS.length };
}

// Total task count across all phases — used by the per-pilot progress chip.
const TOTAL_CHECKLIST_TASKS = PILOT_CHECKLIST.reduce((n, ph) => n + ph.tasks.length, 0);

function checklistProgress(state) {
  const s = state || {};
  let done = 0;
  PILOT_CHECKLIST.forEach((ph, pi) => ph.tasks.forEach((_, ti) => { if (s[`p${pi}-${ti}`]) done++; }));
  return { done, total: TOTAL_CHECKLIST_TASKS };
}

// Expose to the global object so sibling pilots-*.jsx files can read by
// bareword. (Function declarations already auto-globalise; const data
// tables don't, hence the explicit window assignments.)
window.PILOT_STATUSES         = PILOT_STATUSES;
window.pilotStatusTone        = pilotStatusTone;
window.pilotStatusLabel       = pilotStatusLabel;
window.poundsFromPence        = poundsFromPence;
window.fmtPounds              = fmtPounds;
window.addDaysIso             = addDaysIso;
window.PILOT_CHECKLIST        = PILOT_CHECKLIST;
window.PILOT_SOURCES          = PILOT_SOURCES;
window.PILOT_GATES            = PILOT_GATES;
window.PILOT_GATE_DECISIONS   = PILOT_GATE_DECISIONS;
window.pilotGateLabel         = pilotGateLabel;
window.nextPilotGate          = nextPilotGate;
window.createNextGateSafely   = createNextGateSafely;
window.pilotGateDecisionTone  = pilotGateDecisionTone;
window.pilotGateDecisionLabel = pilotGateDecisionLabel;
window.TOTAL_CHECKLIST_TASKS  = TOTAL_CHECKLIST_TASKS;
window.checklistProgress      = checklistProgress;
window.PILOT_CASE_STUDY_SECTIONS = PILOT_CASE_STUDY_SECTIONS;
window.CASE_STUDY_FIELD_KEYS     = CASE_STUDY_FIELD_KEYS;
window.caseStudyProgress         = caseStudyProgress;
